<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import {
  useChatFromPhoneNumber,
  isPhoneNumber,
} from 'dashboard/composables/useChatFromPhoneNumber';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  displayValue: {
    type: String,
    default: '',
  },
  className: {
    type: String,
    default: '',
  },
  contact: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const { startChat, menuItems, isLoading, normalizePhone } =
  useChatFromPhoneNumber();

const showMenu = ref(false);

const phone = computed(() => normalizePhone(props.value));
const label = computed(() => props.displayValue || props.value);
const hasPhoneInboxes = computed(() => menuItems.value.length > 0);
const isClickable = computed(
  () => isPhoneNumber(props.value) && hasPhoneInboxes.value
);

const sectionItems = computed(() => [
  {
    title: t('PHONE_CHAT.CHAT_WITH', { phone: phone.value }),
    description: t('PHONE_CHAT.SELECT_INBOX_HINT'),
    items: menuItems.value,
    isLoading: isLoading.value,
  },
]);

const handleClick = () => {
  if (!isClickable.value) return;
  showMenu.value = !showMenu.value;
};

const handleAction = ({ value, ...rest }) => {
  const inbox =
    menuItems.value.find(item => item.id === value || item.value === value) ||
    rest;
  startChat({ phone: phone.value, inbox, contact: props.contact });
  showMenu.value = false;
};
</script>

<template>
  <div
    v-on-click-outside="() => (showMenu = false)"
    class="relative inline-flex items-center"
  >
    <component
      :is="isClickable ? 'button' : 'span'"
      class="inline-flex items-center gap-1 text-n-slate-11"
      :class="[isClickable ? 'hover:underline cursor-pointer' : '', className]"
      :type="isClickable ? 'button' : undefined"
      @click="handleClick"
    >
      <slot>
        <span class="overflow-hidden whitespace-nowrap text-ellipsis">
          {{ label }}
        </span>
      </slot>
      <Spinner v-if="isLoading" :size="12" />
    </component>

    <DropdownMenu
      v-if="showMenu && sectionItems[0].items.length"
      :menu-sections="sectionItems"
      :show-search="menuItems.length > 5"
      class="absolute z-50 mb-1 ltr:left-0 rtl:right-0 bottom-full w-fit min-w-56 max-w-xs"
      @action="handleAction"
    />
  </div>
</template>
